import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SfmcMobilePush, type SfmcInboxMessage } from 'capacitor-sfmc-mobile-push';
import { buildDummyInboxMessages } from '@/lib/notifications/dummyInboxMessages';
import { cn, useClientTranslation } from '@/lib/core';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { ScreenHead } from '@/components/shared/screen/head';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { ServerError } from '@/components/shared/errors';
import { AppNavBar } from '@/components/layout/navBar';
import { Icon } from '@/components/ui/icon';

type Translate = ReturnType<typeof useClientTranslation>;
type NotificationBucket = 'today' | 'thisWeek' | 'older';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function daysBetween(now: number, sentAt: number): number {
  return Math.floor((startOfDay(now) - startOfDay(sentAt)) / DAY_MS);
}

function bucketFor(sentAt: number | undefined, now: number): NotificationBucket {
  if (!sentAt) return 'older';
  const diffDays = daysBetween(now, sentAt);
  if (diffDays <= 0) return 'today';
  if (diffDays < 7) return 'thisWeek';
  return 'older';
}

function formatRelativeTime(
  sentAt: number | undefined,
  now: number,
  locale: string | undefined,
  t: Translate
) {
  if (!sentAt) return '';
  const diffDays = daysBetween(now, sentAt);

  if (diffDays <= 0) {
    const diffHours = Math.floor((now - sentAt) / (60 * 60 * 1000));
    if (diffHours < 1) {
      const diffMinutes = Math.max(1, Math.floor((now - sentAt) / (60 * 1000)));
      return t('App.Labels.MinutesAgo', { minutes: diffMinutes });
    }
    return t('App.Labels.HoursAgo', { hours: diffHours });
  }
  if (diffDays === 1) return t('App.Labels.Yesterday');
  if (diffDays < 7) return t('App.Labels.DaysAgo', { days: diffDays });
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(new Date(sentAt));
}

interface NotificationCardProps {
  message: SfmcInboxMessage;
  now: number;
  locale: string | undefined;
  t: Translate;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOpen: (message: SfmcInboxMessage) => void;
  onDelete: (message: SfmcInboxMessage) => void;
}

const ACTIONS_PANEL_WIDTH = 44; // single size-11 (44px) delete button
const ACTIONS_PANEL_GAP = 8; // visible gap between the content row and the panel once open
const OPEN_OFFSET = ACTIONS_PANEL_WIDTH + ACTIONS_PANEL_GAP;
const SWIPE_OPEN_THRESHOLD = OPEN_OFFSET / 2;
const DRAG_CLICK_THRESHOLD = 6;

function NotificationCard({
  message,
  now,
  locale,
  t,
  isOpen,
  onOpenChange,
  onOpen,
  onDelete,
}: NotificationCardProps) {
  const isUnread = !message.isRead;
  const [dragX, setDragX] = useState<number | null>(null);
  const dragState = useRef({ startX: 0, baseX: 0, moved: false, active: false });

  const translateX = dragX ?? (isOpen ? -OPEN_OFFSET : 0);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragState.current = {
      startX: e.clientX,
      baseX: isOpen ? -OPEN_OFFSET : 0,
      moved: false,
      active: true,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const state = dragState.current;
    if (!state.active) return;
    const delta = e.clientX - state.startX;
    if (Math.abs(delta) > DRAG_CLICK_THRESHOLD) state.moved = true;
    setDragX(Math.min(0, Math.max(-OPEN_OFFSET, state.baseX + delta)));
  };

  // Decided here rather than from a native "click": setPointerCapture (needed so the drag keeps
  // tracking even if the pointer leaves the row) retargets the browser's synthesized click event
  // to this capturing element, so a click on the nested content button never fires for pointer input.
  const handlePointerUp = () => {
    const state = dragState.current;
    if (!state.active) return;
    state.active = false;
    const finalX = dragX ?? state.baseX;
    setDragX(null);
    if (!state.moved) {
      if (isOpen) onOpenChange(false);
      else onOpen(message);
      return;
    }
    onOpenChange(finalX <= -SWIPE_OPEN_THRESHOLD);
  };

  // Keyboard-only fallback (Enter/Space on the button) — a real click event carries no preceding
  // pointerdown, so it isn't covered by handlePointerUp above. `detail === 0` is how a
  // keyboard-synthesized click is distinguished from a pointer-originated one.
  const handleContentClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (e.detail !== 0) return;
    if (isOpen) {
      onOpenChange(false);
      return;
    }
    onOpen(message);
  };

  // Panel slides in from beyond the right edge as the row is dragged open, instead of
  // sitting static behind the content — panelTranslateX runs 0 (open) to OPEN_OFFSET (hidden).
  const panelTranslateX = translateX + OPEN_OFFSET;
  const transition = dragX === null ? 'transform 200ms ease-out' : 'none';

  return (
    <div className="w-full pb-[16px]">
      <div className="relative w-full overflow-hidden">
        {/* Hidden action panel — slides in from the right as the row is dragged open */}
        <div
          className="bg-jncquoi-neutral-50 absolute inset-y-0 right-0 flex shrink-0 items-center justify-center"
          style={{ transform: `translateX(${panelTranslateX}px)`, transition }}
        >
          <button
            type="button"
            aria-label={t('App.Labels.DeleteNotification')}
            onClick={() => {
              onOpenChange(false);
              onDelete(message);
            }}
            className="text-jncquoi-neutral-1000 flex size-11 items-center justify-center rounded-full"
          >
            <Icon name="bin" width={20} height={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Swipeable content row */}
        <div
          className="bg-jncquoi-neutral-00 relative flex w-full touch-pan-y gap-[8px]"
          style={{ transform: `translateX(${translateX}px)`, transition }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <button
            type="button"
            onClick={handleContentClick}
            className="flex flex-1 flex-col items-start justify-center gap-[8px] text-left"
          >
            <div className="flex w-full items-center gap-[8px]">
              <span
                aria-hidden="true"
                className={cn(
                  'mr-[8px] size-[8px] shrink-0 rounded-full',
                  isUnread ? 'bg-jncquoi-neutral-1000' : 'bg-jncquoi-neutral-400'
                )}
              />
              <span
                className={cn(
                  'font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[14px] tracking-[0.5px]',
                  isUnread && 'font-bold'
                )}
              >
                {message.title}
              </span>
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[14px] tracking-[0.5px] whitespace-nowrap">
                {formatRelativeTime(message.sentAt, now, locale, t)}
              </span>
            </div>
            {message.body && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-800 w-full text-[14px] tracking-[0.5px]">
                {message.body}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NotificationsInbox() {
  const navigate = useNavigate();
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();
  const settingsPath = appSettings?.properties?.profileSettingsNotifications?.route?.path;
  const [messages, setMessages] = useState<SfmcInboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<TranslationKey | null>(null);
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);

  useEffect(() => {
    let isStale = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        if (!Capacitor.isNativePlatform()) {
          if (!isStale) setMessages(buildDummyInboxMessages(Date.now()));
          return;
        }
        const { messages: fetched } = await SfmcMobilePush.getInboxMessages();
        if (!isStale) setMessages(fetched.filter(m => !m.isDeleted));
      } catch (err) {
        console.warn('[NotificationsInbox] getInboxMessages failed:', err);
        if (!isStale) setError('App.Labels.SomethingWentWrong');
      } finally {
        if (!isStale) setLoading(false);
      }
    }

    load();

    return () => {
      isStale = true;
    };
  }, []);

  const handleMarkRead = useCallback((message: SfmcInboxMessage) => {
    if (message.isRead) return;
    setMessages(prev => prev.map(m => (m.id === message.id ? { ...m, isRead: true } : m)));
    if (!Capacitor.isNativePlatform()) return;
    SfmcMobilePush.markMessageRead({ messageId: message.id }).catch(err => {
      console.warn('[NotificationsInbox] markMessageRead failed:', err);
    });
  }, []);

  const handleOpen = useCallback(
    (message: SfmcInboxMessage) => {
      handleMarkRead(message);
      const target = message.customKeys?.deepLinkPath ?? message.url;
      if (target) navigate(target);
    },
    [handleMarkRead, navigate]
  );

  const handleDelete = useCallback((message: SfmcInboxMessage) => {
    setMessages(prev => prev.filter(m => m.id !== message.id));
    if (!Capacitor.isNativePlatform()) return;
    SfmcMobilePush.deleteMessage({ messageId: message.id }).catch(err => {
      console.warn('[NotificationsInbox] deleteMessage failed:', err);
    });
  }, []);

  const now = Date.now();
  const sections = useMemo(() => {
    const sorted = [...messages].sort((a, b) => (b.sentAt ?? 0) - (a.sentAt ?? 0));
    const groups: Record<NotificationBucket, SfmcInboxMessage[]> = { today: [], thisWeek: [], older: [] };
    for (const message of sorted) groups[bucketFor(message.sentAt, now)].push(message);
    return [
      { key: 'today' as const, title: t('App.Labels.Today'), items: groups.today },
      { key: 'thisWeek' as const, title: t('App.Labels.ThisWeek'), items: groups.thisWeek },
      { key: 'older' as const, title: t('App.Labels.Older'), items: groups.older },
    ];
  }, [messages, now, t]);

  const hasMessages = messages.length > 0;

  return (
    <div className="bg-jncquoi-neutral-00 pt-safe-area relative flex min-h-svh flex-col pb-[100px]">
      <div className="relative min-h-15 w-full">
        <div className={cn('pt-safe-area fixed top-0 z-80 flex w-full', 'bg-white')}>
          <ScreenHead
            title=""
            onBack={() => navigate(-1)}
            ariaLabel={t('App.Labels.GoBack')}
            className="pt-0"
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-[56px] px-[16px]">
        <div className="flex w-full items-center">
          <h1 className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[24px] font-semibold">
            {t('App.Labels.YourNotifications')}
          </h1>
          <button
            type="button"
            onClick={() => settingsPath && navigate(settingsPath)}
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 shrink-0 p-4 text-[12px] tracking-[0.5px] uppercase"
          >
            {t('App.Labels.Settings')}
          </button>
        </div>

        {loading && <SpinLoading wrapperClassName="min-h-[30vh]" />}
        {!loading && error && <ServerError error={t(error)} />}

        {!loading && !error && !hasMessages && (
          <p className="text-jncquoi-neutral-700 w-full text-center text-[14px] tracking-[0.5px]">
            {t('App.Labels.NoNotificationsYet')}
          </p>
        )}

        {!loading && !error && hasMessages && (
          <div className="flex w-full flex-col items-start gap-[32px]">
            {sections.map(
              section =>
                section.items.length > 0 && (
                  <Fragment key={section.key}>
                    <h2 className="font-inter text-jncquoi-neutral-900 w-full text-[16px] font-semibold tracking-[0.25px]">
                      {section.title}
                    </h2>
                    {section.items.map(message => (
                      <NotificationCard
                        key={message.id}
                        message={message}
                        now={now}
                        locale={locale}
                        t={t}
                        isOpen={openMessageId === message.id}
                        onOpenChange={open => setOpenMessageId(open ? message.id : null)}
                        onOpen={handleOpen}
                        onDelete={handleDelete}
                      />
                    ))}
                  </Fragment>
                )
            )}
          </div>
        )}
      </div>

      <AppNavBar navStyle="light" />
    </div>
  );
}
